import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import {
  TextField,
  Button,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormLabel,
  FormControl,
  Typography,
  Box
} from '@mui/material';
import { toast } from 'react-toastify';
import { useAuth } from '../hooks/useAuth';
import SocialLoginButtons from '../components/SocialLoginButtons';
import '../styles/pages/Login.scss';

interface LoginFormData {
  email?: string;
  whatsapp?: string;
  password: string;
}

interface LoginData {
  email: string;
  password: string;
  [key: string]: string;
}

const Login: React.FC = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [tipoLogin, setTipoLogin] = useState<'email' | 'whatsapp'>('email');
  const [loading, setLoading] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm<LoginFormData>();

  const onSubmit = async (data: LoginFormData) => {
    setLoading(true);
    try {
      const loginData: LoginData = {
        email: tipoLogin === 'email' ? (data.email || '') : (data.whatsapp || ''),
        password: data.password,
        [tipoLogin]: data[tipoLogin] || ''
      };
      
      await login(loginData);
      toast.success('Login realizado com sucesso!');
      navigate('/membro');
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Erro ao fazer login');
    } finally {
      setLoading(false);
    }
  };

  const handleTipoLoginChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setTipoLogin(event.target.value as 'email' | 'whatsapp');
  };

  return (
    <div className="login-page">
      <Typography variant="h4" component="h1" gutterBottom>
        Entrar na plataforma
      </Typography>
      
      <Box component="form" onSubmit={handleSubmit(onSubmit)} className="login-form">
        <FormControl component="fieldset" className="radio-group">
          <FormLabel component="legend">Logar com</FormLabel>
          <RadioGroup
            value={tipoLogin}
            onChange={handleTipoLoginChange}
            row
          >
            <FormControlLabel 
              value="email" 
              control={<Radio />} 
              label="E-mail" 
            />
            <FormControlLabel 
              value="whatsapp" 
              control={<Radio />} 
              label="Whatsapp" 
            />
          </RadioGroup>
        </FormControl>

        {tipoLogin === 'whatsapp' && (
          <TextField
            {...register('whatsapp', { 
              required: 'Por favor digite seu Whatsapp',
              pattern: {
                value: /^\d+$/,
                message: 'Digite apenas números'
              }
            })}
            label="Whatsapp"
            type="number"
            placeholder="Seu número de Whatsapp"
            helperText="Ex.: 551198887770"
            error={!!errors.whatsapp}
            fullWidth
            margin="normal"
            variant="outlined"
          />
        )}

        {tipoLogin === 'email' && (
          <TextField
            {...register('email', { 
              required: 'Por favor digite seu e-mail',
              pattern: {
                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                message: 'Digite um e-mail válido'
              }
            })}
            label="E-mail"
            type="email"
            placeholder="Seu e-mail"
            error={!!errors.email}
            fullWidth
            margin="normal"
            variant="outlined"
          />
        )}

        <TextField
          {...register('password', { 
            required: 'Digite sua senha',
            minLength: {
              value: 6,
              message: 'A senha deve ter pelo menos 6 caracteres'
            }
          })}
          label="Senha"
          type="password"
          placeholder="Digite sua senha"
          error={!!errors.password}
          helperText={errors.password?.message}
          fullWidth
          margin="normal"
          variant="outlined"
        />

        <Button
          type="submit"
          variant="contained"
          color="primary"
          fullWidth
          size="large"
          disabled={loading}
          className="btn-entrar"
        >
          {loading ? 'Entrando...' : 'Entrar'}
        </Button>

        <Button
          component={Link}
          to="/inicio/esqueci-senha"
          variant="outlined"
          color="primary"
          fullWidth
          size="large"
        >
          Esqueci minha senha
        </Button>

        <SocialLoginButtons 
          variant="login"
          disabled={loading}
        />

        <Typography variant="body2" align="center" className="cadastro-text">
          ainda não tem cadastro?
        </Typography>

        <Button
          component={Link}
          to="/inicio/cadastro"
          variant="contained"
          color="secondary"
          fullWidth
          size="large"
        >
          clique aqui e cadastre-se
        </Button>
      </Box>
    </div>
  );
};

export default Login; 